import Joi from 'joi'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../../../UI/Btn'
import TextField from '../../../../../../UI/TextField'

import SettingHint from '../SettingHint'
import TemplateLinks from '../TemplateLinks'

import styles from '../style.module.scss'

/*
 * Meldungen auf Deutsch und feldbezogen — wie bei den Erinnerungen an offene
 * Betreuungsanfragen. Ohne eigene Texte zeigt Joi den Rohtext samt Feldpfad an
 * und der Satz ist für die Person vor der Maske nicht verwertbar.
 */
const schema = Joi.object({
	supporterApprovalReminders: Joi.object().keys({
		dueDays: Joi.number().integer().min(1).max(90).required().messages({
			'number.base': 'Bitte eine Frist in Tagen angeben.',
			'number.integer': 'Bitte eine Frist in ganzen Tagen angeben.',
			'number.min': 'Bitte eine Frist von mindestens 1 Tag angeben.',
			'number.max': 'Bitte eine Frist von höchstens 90 Tagen angeben.',
			'any.required': 'Bitte eine Frist in Tagen angeben.'
		}),
		frequencyDays: Joi.number().integer().min(1).max(90).required().messages({
			'number.base': 'Bitte einen Takt in Tagen angeben.',
			'number.integer': 'Bitte einen Takt in ganzen Tagen angeben.',
			'number.min': 'Bitte einen Takt von mindestens 1 Tag angeben.',
			'number.max': 'Bitte einen Takt von höchstens 90 Tagen angeben.',
			'any.required': 'Bitte einen Takt in Tagen angeben.'
		})
	})
})

/**
 * Erinnerung an Selbstregistrierungen, die auf ihre Freigabe warten
 * (Anforderung #58).
 *
 * Wer sich selbst registriert, ist für Schulen unsichtbar, bis die
 * Bundesadministration das Profil freigibt — und hört bis dahin nichts. Ohne
 * diese Erinnerung bleibt eine Anmeldung liegen, ohne dass es irgendwo
 * auffällt.
 */
export default function SupporterApprovalReminders(props) {
	const { t } = useTranslation()
	const {
		register,
		reset,
		handleSubmit,
		formState: { errors }
	} = useForm({ resolver: joiResolver(schema) })

	const onSubmit = form => {
		props
			.updateOptions({
				name: 'supporterApprovalReminders',
				// „enabled" ist bewusst nicht im Formular — es gibt keinen Grund,
				// wartende Anmeldungen gar nicht mehr zu melden. Der vorhandene Wert
				// bleibt erhalten.
				value: {
					...(props.form?.supporterApprovalReminders || {}),
					dueDays: Number(form.supporterApprovalReminders.dueDays),
					frequencyDays: Number(form.supporterApprovalReminders.frequencyDays)
				}
			})
			.then(() => toast.success(t('Options was updated')))
	}

	useEffect(() => {
		const value = props.form?.supporterApprovalReminders || {}
		reset({
			supporterApprovalReminders: {
				dueDays: value.dueDays ?? 7,
				frequencyDays: value.frequencyDays ?? 7
			}
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.form])

	return (
		<form id="supporterApprovalReminders" className={styles.root} onSubmit={handleSubmit(onSubmit)}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>Erinnerung an offene Freigaben</h3>
				<SettingHint
					example="Beispiel: nach 7 Tagen erinnern und danach wöchentlich — so steht eine wartende Anmeldung spätestens nach einer Woche in einer E-Mail."
					warning="Die Erinnerung geht ausschließlich an die Bundesadministration: Dort liegt die Freigabe. Der Lauf findet einmal täglich in der Nacht statt."
				>
					Wer sich über die öffentliche Seite als Unterstützer:in registriert, ist für Schulen unsichtbar, bis
					die Bundesadministration das Profil freigibt — und erfährt bis dahin nichts. Wartet eine Anmeldung
					länger als die hier eingestellte Frist, geht eine Sammelmail mit allen offenen Freigaben hinaus.
				</SettingHint>
				<TemplateLinks items={[{ channel: 'email', key: 'SUPPORTER_APPROVAL_PENDING' }]} />
				<div className={styles.sectionContent}>
					<div className={styles.flexContainer}>
						<TextField
							className={styles.field}
							label="Erste Erinnerung nach [x] Tagen"
							mb={20}
							register={register('supporterApprovalReminders.dueDays')}
							error={errors.supporterApprovalReminders?.dueDays}
						/>
						<TextField
							className={styles.field}
							label="Danach erneut erinnern alle [x] Tage"
							mb={20}
							register={register('supporterApprovalReminders.frequencyDays')}
							error={errors.supporterApprovalReminders?.frequencyDays}
						/>
					</div>
					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

SupporterApprovalReminders.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}
